import {
  GroupMember,
  type GroupMemberList,
  OrganizationGroup,
  type OrganizationGroupList,
  Role,
  RoleCategory,
  type RoleCategoryList,
  RoleDepartment,
  type RoleDepartmentList,
  type RoleList,
} from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { UserClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { useBatchUserQuery } from './useUserQuery';

/**
 * How long org structure reads stay fresh.
 *
 * These queries set no staleTime before, which with the app's global
 * `refetchOnWindowFocus` meant every return to the tab refetched all of them —
 * teams, groups, job titles, levels, memberships and the whole active-employee
 * batch. Each refetch also hands back new `data`, which rebuilds every derived
 * map downstream even when nothing changed.
 *
 * A minute is chosen over something longer because two admins can be editing
 * this at once and should see each other's work without reloading. It costs
 * nothing in correctness: every mutation here invalidates what it touched, and
 * an invalidated query refetches regardless of how fresh this says it is.
 */
const ORG_STALE_MS = 60_000;

/**
 * Every active employee, as the org chart and its admin screen read them.
 *
 * One hook rather than the same filter block written out at each call site:
 * those four reads have to agree on the filters to share a cache entry, and on
 * staleTime to stop a window focus refetching four hundred rows, because both
 * are per observer. Agreeing by construction beats a comment asking them to.
 */
export const useOrgEmployeesQuery = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useBatchUserQuery({
    filters: {
      isEmployee: 1,
      isActive: 1,
      overrideLimit: true,
      orderBy: 'user_lastname',
      orderDir: 'ASC',
    },
    enabled,
    options: { staleTime: ORG_STALE_MS },
  });

/**
 * Data layer for the org chart's structural entities (ClickUp 86bbg031y).
 *
 * Four entities, deliberately in one file because the UI always uses them
 * together: a role belongs to a category, and a membership joins a user to a
 * group with an optional role.
 *
 * Reporting structure is NOT here — it comes from userz.managedBy and is read
 * through the existing user queries. These entities only add grouping and titles.
 */

// ─── Role categories (the level ladder) ──────────────────────────────

export const useRoleCategoryListQuery = ({
  enabled = true,
  options,
}: {
  enabled?: boolean;
  options?: Partial<UseQueryOptions<RoleCategoryList, Error>>;
} = {}) =>
  useQuery({
    queryFn: async () => await UserClientService.BatchGetRoleCategory(RoleCategory.create()),
    queryKey: [queryKeys.roleCategory.root, queryKeys.roleCategory.list],
    enabled,
    staleTime: ORG_STALE_MS,
    ...options,
  });

/** Categories ordered by rung, highest first — the order the chart renders in. */
export const useRoleCategoriesByRank = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data, ...rest } = useRoleCategoryListQuery({ enabled });
  const categories = useMemo(
    () =>
      // Name breaks a tie so the ladder cannot reshuffle between renders: two
      // rungs sharing a sort_order is bad data, but an unstable order on top of
      // it makes the reorder arrows move the wrong row.
      [...(data?.results ?? [])].sort(
        (a, b) =>
          b.sortOrder - a.sortOrder ||
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    [data],
  );
  return { categories, ...rest };
};

export const useRoleCategoryCreateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; sortOrder: number; description?: string }) =>
      await UserClientService.CreateRoleCategory(RoleCategory.create({ ...input, isActive: true })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [queryKeys.roleCategory.root] }),
  });
};

export const useRoleCategoryUpdateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<RoleCategory> & { id: number; fieldMask: string[] }) =>
      await UserClientService.UpdateRoleCategory(RoleCategory.create(input)),
    // Patched in place rather than invalidated, for the reason given on the
    // group update below. A reorder writes the whole ladder one row at a time,
    // so a forced refetch between two of those writes repaints the ladder
    // half-renumbered — which is why moving a level twice appeared to do
    // nothing the second time.
    onMutate: async (input) => {
      const { fieldMask: _fieldMask, ...changed } = input;
      let previous: RoleCategory | undefined;
      qc.setQueriesData<RoleCategoryList>({ queryKey: [queryKeys.roleCategory.root] }, (old) =>
        old
          ? {
              ...old,
              results: (old.results ?? []).map((row) => {
                if (row.id !== input.id) return row;
                previous = row;
                return RoleCategory.create({ ...row, ...changed });
              }),
            }
          : old,
      );
      await qc.cancelQueries({ queryKey: [queryKeys.roleCategory.root] });
      return { previous };
    },
    onError: (_error, _input, context) => {
      const previous = context?.previous;
      if (!previous) return;
      qc.setQueriesData<RoleCategoryList>({ queryKey: [queryKeys.roleCategory.root] }, (old) =>
        old
          ? {
              ...old,
              results: (old.results ?? []).map((row) => (row.id === previous.id ? previous : row)),
            }
          : old,
      );
    },
    onSettled: () =>
      void qc.invalidateQueries({ queryKey: [queryKeys.roleCategory.root], refetchType: 'none' }),
  });
};

// ─── Roles (the managed job-title list) ──────────────────────────────

export const useRoleListQuery = ({
  departmentId,
  enabled = true,
  options,
}: {
  departmentId?: number;
  enabled?: boolean;
  options?: Partial<UseQueryOptions<RoleList, Error>>;
} = {}) =>
  useQuery({
    queryFn: async () =>
      await UserClientService.BatchGetRole(Role.create(departmentId ? { departmentId } : {})),
    queryKey: [queryKeys.role.root, queryKeys.role.list, queryKeys.role.getHash(departmentId)],
    enabled,
    staleTime: ORG_STALE_MS,
    ...options,
  });

/**
 * Roles for a picker, ordered by rung then name.
 *
 * `departmentId` filters the *suggestions* only — every active role stays
 * selectable, because titles legitimately span departments (Procurement
 * Assistant appears in five). Filtering server-side would hide valid choices.
 */
export const useRolesForPicker = ({
  departmentId = 0,
  enabled = true,
}: {
  departmentId?: number;
  enabled?: boolean;
} = {}) => {
  const { data, ...rest } = useRoleListQuery({ enabled });
  const { categories } = useRoleCategoriesByRank({ enabled });
  // Departments live in role_department now. The legacy role.department_id is
  // still read as a fallback for titles nobody has edited since, but anything
  // set on the Job Titles tab writes rows here and leaves that column alone —
  // so matching on it alone suggested nothing.
  const { departmentsByRole } = useDepartmentsByRole({ enabled });

  const roles = useMemo(() => {
    const rank = new Map(categories.map((c) => [c.id, c.sortOrder]));
    const all = (data?.results ?? []).filter((r) => r.isActive);
    const servesDepartment = (role: Role) =>
      departmentId > 0 &&
      ((departmentsByRole.get(role.id) ?? []).includes(departmentId) ||
        role.departmentId === departmentId);

    return [...all].sort((a, b) => {
      // Suggested (serving this department) first, then rung desc, then name.
      if (departmentId) {
        const aMatch = servesDepartment(a) ? 0 : 1;
        const bMatch = servesDepartment(b) ? 0 : 1;
        if (aMatch !== bMatch) return aMatch - bMatch;
      }
      const ra = rank.get(a.roleCategoryId) ?? -1;
      const rb = rank.get(b.roleCategoryId) ?? -1;
      if (ra !== rb) return rb - ra;
      // Stated order within a level, then name. sort_order is 0 until a level's
      // titles are arranged, so an unarranged level still reads alphabetically.
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [data, categories, departmentId, departmentsByRole]);

  return { roles, ...rest };
};

/** Archived job titles, so the screen can offer to restore one. */
export const useRetiredRoles = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data, ...rest } = useRoleListQuery({ enabled });
  const retired = useMemo(
    () =>
      (data?.results ?? [])
        .filter((r) => !r.isActive)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [data],
  );
  return { retired, ...rest };
};

export const useRoleCreateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; roleCategoryId: number; departmentId?: number }) =>
      await UserClientService.CreateRole(Role.create({ ...input, isActive: true })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [queryKeys.role.root] }),
  });
};

export const useRoleUpdateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Role> & { id: number; fieldMask: string[] }) =>
      await UserClientService.UpdateRole(Role.create(input)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [queryKeys.role.root] }),
  });
};

// ─── Role departments (which departments a job title applies to) ─────

export const useRoleDepartmentListQuery = ({
  enabled = true,
  options,
}: {
  enabled?: boolean;
  options?: Partial<UseQueryOptions<RoleDepartmentList, Error>>;
} = {}) =>
  useQuery({
    queryFn: async () => await UserClientService.BatchGetRoleDepartment(RoleDepartment.create()),
    queryKey: [queryKeys.roleDepartment.root, queryKeys.roleDepartment.list],
    enabled,
    staleTime: ORG_STALE_MS,
    ...options,
  });

/** Department ids per role, for the Job Titles screen. */
export const useDepartmentsByRole = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data, ...rest } = useRoleDepartmentListQuery({ enabled });

  // Deduplicated: the table has no unique key on (role, department), and a
  // title can carry the same department twice — rows an earlier bug wrote, or
  // two people ticking it at once. One department, listed once.
  const departmentsByRole = useMemo(() => {
    const out = new Map<number, number[]>();
    for (const rd of data?.results ?? []) {
      const seen = out.get(rd.roleId) ?? [];
      if (!seen.includes(rd.departmentId)) out.set(rd.roleId, [...seen, rd.departmentId]);
    }
    return out;
  }, [data]);

  /**
   * Every row behind one role-and-department, so removing it removes all of
   * them. Keeping only the last meant a duplicate survived the click: the
   * department came back and had to be removed a second time.
   */
  const rowIdsFor = useMemo(() => {
    const out = new Map<string, number[]>();
    for (const rd of data?.results ?? []) {
      const key = `${rd.roleId}:${rd.departmentId}`;
      out.set(key, [...(out.get(key) ?? []), rd.id]);
    }
    return out;
  }, [data]);

  return { departmentsByRole, rowIdsFor, ...rest };
};

/**
 * Apply a department change to the cached list.
 *
 * The picker keeps its own selection and syncs it back to this data whenever
 * the row re-renders — and the row re-renders the moment a mutation starts,
 * because its handlers depend on the mutation objects and those change
 * identity then. So a patch applied on SUCCESS is too late: the sync has
 * already thrown the tick away against data that does not carry it yet. Hence
 * onMutate.
 *
 * The refetch is not forced either, only marked stale: reading straight after
 * the write answered with the row as it was before it, which took the change
 * off the screen a second time.
 */
const patchRoleDepartmentCache = (
  qc: ReturnType<typeof useQueryClient>,
  patch: (rows: RoleDepartment[]) => RoleDepartment[],
) =>
  qc.setQueriesData<RoleDepartmentList>({ queryKey: [queryKeys.roleDepartment.root] }, (old) =>
    old ? { ...old, results: patch(old.results ?? []) } : old,
  );

const markRoleDepartmentsStale = (qc: ReturnType<typeof useQueryClient>) =>
  void qc.invalidateQueries({ queryKey: [queryKeys.roleDepartment.root], refetchType: 'none' });

export const useRoleDepartmentCreateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { roleId: number; departmentId: number }) =>
      await UserClientService.CreateRoleDepartment(RoleDepartment.create(input)),
    onMutate: async (input) => {
      // Patch FIRST, await second. Awaiting anything here yields to React, and
      // a render in that gap sees the old data and syncs the picker back to
      // it — which is the flicker this whole path exists to avoid.
      //
      // A placeholder id until the real one arrives — negative, so it cannot
      // collide with a row the server gave us.
      const pendingId = -Date.now();
      patchRoleDepartmentCache(qc, (rows) => [
        ...rows,
        RoleDepartment.create({ ...input, id: pendingId }),
      ]);
      // An in-flight read would land on top of the patch, so it is stopped.
      await qc.cancelQueries({ queryKey: [queryKeys.roleDepartment.root] });
      return { pendingId };
    },
    onError: (_error, _input, context) =>
      patchRoleDepartmentCache(qc, (rows) => rows.filter((r) => r.id !== context?.pendingId)),
    onSuccess: (created, _input, context) =>
      patchRoleDepartmentCache(qc, (rows) =>
        rows.map((r) => (r.id === context?.pendingId && created?.id ? created : r)),
      ),
    onSettled: () => markRoleDepartmentsStale(qc),
  });
};

export const useRoleDepartmentDeleteMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) =>
      await UserClientService.DeleteRoleDepartment(RoleDepartment.create({ id })),
    onMutate: async (id) => {
      // Patched before the await, for the reason given on the create above.
      const removed: RoleDepartment[] = [];
      patchRoleDepartmentCache(qc, (rows) =>
        rows.filter((r) => {
          if (r.id !== id) return true;
          removed.push(r);
          return false;
        }),
      );
      await qc.cancelQueries({ queryKey: [queryKeys.roleDepartment.root] });
      return { removed };
    },
    onError: (_error, _id, context) =>
      patchRoleDepartmentCache(qc, (rows) => [...rows, ...(context?.removed ?? [])]),
    onSettled: () => markRoleDepartmentsStale(qc),
  });
};

/**
 * The team ids an employee currently holds, and a way to save a new set.
 *
 * `stored` is what a form initialises from and compares against to know
 * whether the field is dirty; `apply` writes only the difference, so an
 * untouched selection sends nothing.
 */
export const useApplyTeamChanges = (userId: number) => {
  const { memberships } = useMembershipsForUser({ userId });
  const createMembership = useGroupMemberCreateMutation();
  const deleteMembership = useGroupMemberDeleteMutation();

  const stored = useMemo(
    () => memberships.map((m) => m.organizationGroupId).sort((a, b) => a - b),
    [memberships],
  );

  const apply = useCallback(
    async (next: number[]) => {
      const wanted = new Set(next);
      const held = new Set(stored);

      // One at a time: these are separate rows and the list they are computed
      // from is refetched on each success.
      for (const m of memberships) {
        if (!wanted.has(m.organizationGroupId)) await deleteMembership.mutateAsync(m.id);
      }
      for (const teamId of next) {
        if (!held.has(teamId)) {
          await createMembership.mutateAsync({ userId, organizationGroupId: teamId });
        }
      }
    },
    [memberships, stored, userId, createMembership, deleteMembership],
  );

  return { stored, apply };
};

/** What the directory can be narrowed to, beyond its own fields. */
export type OrgFilter = 'none' | 'team' | 'missing-title' | 'missing-team' | 'missing-supervisor';

/**
 * The employees matching an org filter, as a `user_id_list` value
 * (ClickUp 86bbfvuxk).
 *
 * A team is a `group_member` row and "missing" is the absence of a value, so
 * neither is a column the directory's own query can filter on. Rather than
 * teaching the RPC to join, the set is worked out here from lists the screen
 * already holds and passed as ids — `user_id_list` is an existing field that
 * pbsql expands to `user_id IN (…)`, so paging and the total count still come
 * from the server.
 *
 * Returns undefined when there is nothing to narrow by, and '0' when the answer
 * is genuinely nobody: an empty list would read as "no filter" and show
 * everyone, which is the opposite of what was asked.
 */
export const useOrgFilterUserIds = ({
  filter,
  teamId = 0,
  enabled = true,
}: {
  filter: OrgFilter;
  teamId?: number;
  enabled?: boolean;
}) => {
  const active = enabled && filter !== 'none';
  const needsEmployees = active && filter !== 'team';

  const usersQuery = useOrgEmployeesQuery({ enabled: needsEmployees });
  const needsMembers = active && filter !== 'missing-title' && filter !== 'missing-supervisor';
  const { data: memberData } = useGroupMemberListQuery({ enabled: needsMembers });
  // The groups beneath a team, for the team filter: people belong to the groups
  // on a team's page rather than to the team row itself, so matching the id
  // alone would return nobody for any team that has been drawn.
  const { data: groupData } = useOrganizationGroupListQuery({
    enabled: active && filter === 'team',
  });

  const userIdList = useMemo(() => {
    // Disabled means the caller cannot use these filters at all, so the answer
    // is "no filter" rather than "nobody" — the queries behind it have not run,
    // and an empty set would hide every employee instead of ignoring a
    // parameter that should not have reached them.
    if (!enabled || filter === 'none') return undefined;

    const memberships = (memberData?.results ?? []).filter((m) => m.isActive);
    const employees = usersQuery.data?.results ?? [];

    let ids: number[] = [];
    if (filter === 'team') {
      if (!teamId) return undefined;

      // The team and everything nested under it, however deep.
      const childrenOf = new Map<number, number[]>();
      for (const g of groupData?.results ?? []) {
        if (!g.isActive || !g.parentGroupId) continue;
        childrenOf.set(g.parentGroupId, [...(childrenOf.get(g.parentGroupId) ?? []), g.id]);
      }
      const wanted = new Set<number>();
      const walk = (id: number) => {
        if (wanted.has(id)) return;
        wanted.add(id);
        for (const child of childrenOf.get(id) ?? []) walk(child);
      };
      walk(teamId);

      ids = memberships.filter((m) => wanted.has(m.organizationGroupId)).map((m) => m.userId);
    } else if (filter === 'missing-team') {
      const onATeam = new Set(memberships.map((m) => m.userId));
      ids = employees.filter((u) => !onATeam.has(u.id)).map((u) => u.id);
    } else if (filter === 'missing-title') {
      ids = employees.filter((u) => !u.roleId).map((u) => u.id);
    } else if (filter === 'missing-supervisor') {
      // Supervising yourself is the legacy way of recording nobody.
      ids = employees.filter((u) => !u.managedBy || u.managedBy === u.id).map((u) => u.id);
    }

    // Only ever digits and commas. pbsql interpolates a multi_value straight
    // into the predicate rather than binding it, so anything else here would be
    // an injection.
    const numeric = [...new Set(ids)].filter((id) => Number.isInteger(id) && id > 0);
    return numeric.length > 0 ? numeric.join(',') : '0';
  }, [enabled, filter, teamId, memberData, usersQuery.data, groupData]);

  // The list is not ready until the query behind it has answered; filtering on
  // a half-built set would quietly show the wrong people.
  const isPending = needsEmployees && usersQuery.isPending;

  return { userIdList, isPending };
};

// ─── Counts across the structure ─────────────────────────────────────

/**
 * Headcounts for the Manage Organization screen (ClickUp 86bbfvuxk).
 *
 * One place for every count the screen shows, because they all come from the
 * same three lists and computing them per tab would fetch each list twice.
 *
 * A person's job title is `userz.role_id`; a level's headcount is the sum of
 * its titles', since nobody carries a level directly.
 */
export const useOrgCounts = ({ enabled = true }: { enabled?: boolean } = {}) => {
  // Same filters as the org chart's employee fetch so the two share a cache
  // entry rather than each paying for the list.
  const usersQuery = useOrgEmployeesQuery({ enabled });
  const { data: memberData } = useGroupMemberListQuery({ enabled });
  const { pages: teams } = useOrganizationGroups({ enabled });
  const { data: roleData } = useRoleListQuery({ enabled });
  const { categories } = useRoleCategoriesByRank({ enabled });

  return useMemo(() => {
    const employees = usersQuery.data?.results ?? [];
    const roles = (roleData?.results ?? []).filter((r) => r.isActive);

    const employeesByRole = new Map<number, number>();
    for (const u of employees) {
      if (u.roleId) employeesByRole.set(u.roleId, (employeesByRole.get(u.roleId) ?? 0) + 1);
    }

    // A level's headcount is the sum of its titles'.
    const titlesByCategory = new Map<number, number>();
    const employeesByCategory = new Map<number, number>();
    for (const r of roles) {
      titlesByCategory.set(r.roleCategoryId, (titlesByCategory.get(r.roleCategoryId) ?? 0) + 1);
      employeesByCategory.set(
        r.roleCategoryId,
        (employeesByCategory.get(r.roleCategoryId) ?? 0) + (employeesByRole.get(r.id) ?? 0),
      );
    }

    const employeesByTeam = new Map<number, number>();
    const onATeam = new Set<number>();
    for (const m of memberData?.results ?? []) {
      if (!m.isActive) continue;
      onATeam.add(m.userId);
      employeesByTeam.set(
        m.organizationGroupId,
        (employeesByTeam.get(m.organizationGroupId) ?? 0) + 1,
      );
    }

    // The gaps the screen warns about. A supervisor pointing at yourself is the
    // legacy way of saying "nobody", so it counts as missing.
    const missingJobTitle = employees.filter((u) => !u.roleId).length;
    const missingTeam = employees.filter((u) => !onATeam.has(u.id)).length;
    const missingSupervisor = employees.filter((u) => !u.managedBy || u.managedBy === u.id).length;

    return {
      employees: employees.length,
      teams: teams.length,
      jobTitles: roles.length,
      levels: categories.length,
      employeesByRole,
      employeesByTeam,
      employeesByCategory,
      titlesByCategory,
      missingJobTitle,
      missingTeam,
      missingSupervisor,
    };
  }, [usersQuery.data, memberData, teams, roleData, categories]);
};

// ─── Organization groups (chart pages and the boxes inside them) ─────

export const useOrganizationGroupListQuery = ({
  enabled = true,
  options,
}: {
  enabled?: boolean;
  options?: Partial<UseQueryOptions<OrganizationGroupList, Error>>;
} = {}) =>
  useQuery({
    queryFn: async () =>
      await UserClientService.BatchGetOrganizationGroup(OrganizationGroup.create()),
    queryKey: [queryKeys.organizationGroup.root, queryKeys.organizationGroup.list],
    enabled,
    staleTime: ORG_STALE_MS,
    ...options,
  });

/**
 * Groups split by role in the chart: a group with no parent is its own page,
 * one with a parent renders as a named box inside that parent's page.
 */
export const useOrganizationGroups = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data, ...rest } = useOrganizationGroupListQuery({ enabled });

  // Memoise on `data` alone. `rest` is a fresh object every render, so including
  // it would defeat the memo and hand every consumer new references each pass —
  // which cascades into an effect that resets state and re-renders forever.
  const derived = useMemo(() => {
    const all = (data?.results ?? []).filter((g) => g.isActive);
    const byName = (a: OrganizationGroup, b: OrganizationGroup) =>
      a.sortOrder !== b.sortOrder
        ? a.sortOrder - b.sortOrder
        : a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

    const pages = all.filter((g) => !g.parentGroupId).sort(byName);
    const childrenByParent = new Map<number, OrganizationGroup[]>();
    for (const g of all.filter((g) => g.parentGroupId)) {
      const list = childrenByParent.get(g.parentGroupId) ?? [];
      list.push(g);
      childrenByParent.set(g.parentGroupId, list);
    }
    for (const [, list] of childrenByParent) list.sort(byName);

    // Full ancestry, for anywhere a group is named outside the chart itself.
    // Box names repeat by design — every crew has an "L3 ES Techs" — so on the
    // chart position tells them apart, but in a flat list or a dropdown the bare
    // name identifies nothing.
    const byId = new Map(all.map((g) => [g.id, g]));
    const pathById = new Map<number, string>();
    for (const group of all) {
      const segments: string[] = [];
      const seen = new Set<number>();
      let current: OrganizationGroup | undefined = group;
      while (current && !seen.has(current.id)) {
        seen.add(current.id);
        segments.unshift(current.name);
        current = current.parentGroupId ? byId.get(current.parentGroupId) : undefined;
      }
      pathById.set(group.id, segments.join(' › '));
    }

    // Sibling boxes legitimately share a name — the Electrical page has three
    // called "ES Lead Superintendent", one per lead, exactly as the slide draws
    // them — so their paths collide as well. Reported rather than papered over,
    // because the caller usually has something better to disambiguate with.
    const pathCounts = new Map<string, number>();
    for (const path of pathById.values()) {
      pathCounts.set(path, (pathCounts.get(path) ?? 0) + 1);
    }
    const ambiguousPathIds = new Set<number>();
    for (const group of all) {
      if ((pathCounts.get(pathById.get(group.id) ?? group.name) ?? 0) > 1) {
        ambiguousPathIds.add(group.id);
      }
    }

    // Retired boxes, so they can be restored. Retiring a box used to strand
    // everything inside it — the children still point at a parent the chart no
    // longer loads, so a whole branch goes invisible with no way back to it
    // from the UI. Retirement has to be reversible for that not to read as data
    // loss.
    const retired = (data?.results ?? [])
      .filter((g) => !g.isActive)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    return {
      groups: all,
      pages,
      childrenByParent,
      groupById: byId,
      pathById,
      ambiguousPathIds,
      retired,
    };
  }, [data]);

  return { ...derived, ...rest };
};

export const useOrganizationGroupCreateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      parentGroupId?: number;
      sortOrder?: number;
      departmentId?: number;
    }) =>
      await UserClientService.CreateOrganizationGroup(
        OrganizationGroup.create({ sortOrder: 0, ...input, isActive: true }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [queryKeys.organizationGroup.root] }),
  });
};

/**
 * Patch the cached group list in place, for the same reason the role-department
 * path does: a write that only invalidates leaves the old value on screen until
 * a round trip completes, and a read issued in that gap can answer with the row
 * as it was and undo the change a second time.
 *
 * Archiving and restoring made that visible. Both go through this one mutation,
 * so a refetch started by an earlier archive would land on top of a later
 * restore and put the team back under "Archived".
 */
const patchOrganizationGroupCache = (
  qc: ReturnType<typeof useQueryClient>,
  patch: (rows: OrganizationGroup[]) => OrganizationGroup[],
) =>
  qc.setQueriesData<OrganizationGroupList>(
    { queryKey: [queryKeys.organizationGroup.root] },
    (old) => (old ? { ...old, results: patch(old.results ?? []) } : old),
  );

export const useOrganizationGroupUpdateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<OrganizationGroup> & { id: number; fieldMask: string[] }) =>
      await UserClientService.UpdateOrganizationGroup(OrganizationGroup.create(input)),
    onMutate: async (input) => {
      // Patch FIRST, await second — awaiting yields to React, and a render in
      // that gap would paint the pre-change row.
      const { fieldMask: _fieldMask, ...changed } = input;
      let previous: OrganizationGroup | undefined;
      patchOrganizationGroupCache(qc, (rows) =>
        rows.map((row) => {
          if (row.id !== input.id) return row;
          previous = row;
          return OrganizationGroup.create({ ...row, ...changed });
        }),
      );
      // An in-flight read would land on top of the patch, so it is stopped.
      // This is what serialises rapid archive/restore clicks.
      await qc.cancelQueries({ queryKey: [queryKeys.organizationGroup.root] });
      return { previous };
    },
    onError: (_error, _input, context) => {
      const previous = context?.previous;
      if (!previous) return;
      patchOrganizationGroupCache(qc, (rows) =>
        rows.map((row) => (row.id === previous.id ? previous : row)),
      );
    },
    // Marked stale rather than refetched, so the next natural read picks up the
    // server's version instead of one racing the patch above.
    onSettled: () =>
      void qc.invalidateQueries({
        queryKey: [queryKeys.organizationGroup.root],
        refetchType: 'none',
      }),
  });
};

// ─── Group membership ────────────────────────────────────────────────

export const useGroupMemberListQuery = ({
  userId,
  organizationGroupId,
  enabled = true,
  options,
}: {
  userId?: number;
  organizationGroupId?: number;
  enabled?: boolean;
  options?: Partial<UseQueryOptions<GroupMemberList, Error>>;
} = {}) =>
  useQuery({
    queryFn: async () =>
      await UserClientService.BatchGetGroupMember(
        GroupMember.create({
          ...(userId ? { userId } : {}),
          ...(organizationGroupId ? { organizationGroupId } : {}),
        }),
      ),
    queryKey: [
      queryKeys.groupMember.root,
      queryKeys.groupMember.list,
      queryKeys.groupMember.getHash({ userId, organizationGroupId }),
    ],
    enabled,
    staleTime: ORG_STALE_MS,
    ...options,
  });

/** One person's active memberships — what the per-employee module edits. */
export const useMembershipsForUser = ({
  userId,
  enabled = true,
}: {
  userId: number;
  enabled?: boolean;
}) => {
  const { data, ...rest } = useGroupMemberListQuery({ userId, enabled: enabled && userId > 0 });
  const memberships = useMemo(() => (data?.results ?? []).filter((m) => m.isActive), [data]);
  return { memberships, ...rest };
};

/**
 * Apply a membership change to the cached lists straight away.
 *
 * Invalidating alone leaves a window: until the refetch lands, every reader
 * still sees the row as it was. Remove someone from a box and put them back
 * inside that window and the caller finds a row that still reads active, so it
 * decides there is nothing to do and writes nothing — silently, since no
 * request was made to fail.
 */
const patchMembershipCache = (
  qc: ReturnType<typeof useQueryClient>,
  patch: (rows: GroupMember[]) => GroupMember[],
) =>
  qc.setQueriesData<GroupMemberList>({ queryKey: [queryKeys.groupMember.root] }, (old) =>
    old ? { ...old, results: patch(old.results ?? []) } : old,
  );

// Marked stale, not refetched now: a read straight after the write can still
// answer with the row as it was, which showed the change, took it away, then
// brought it back.
const markMembershipsStale = (qc: ReturnType<typeof useQueryClient>) =>
  void qc.invalidateQueries({ queryKey: [queryKeys.groupMember.root], refetchType: 'none' });

export const useGroupMemberCreateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: number; organizationGroupId: number; roleId?: number }) =>
      await UserClientService.CreateGroupMember(GroupMember.create({ ...input, isActive: true })),
    onMutate: async (input) => {
      // Patch FIRST, await second — see the role-department create above.
      // A negative placeholder id until the real one arrives, so it cannot
      // collide with a row the server gave us.
      const pendingId = -Date.now();
      patchMembershipCache(qc, (rows) => [
        ...rows,
        GroupMember.create({ ...input, id: pendingId, isActive: true }),
      ]);
      await qc.cancelQueries({ queryKey: [queryKeys.groupMember.root] });
      return { pendingId };
    },
    onError: (_error, _input, context) =>
      patchMembershipCache(qc, (rows) => rows.filter((r) => r.id !== context?.pendingId)),
    onSuccess: (created, _input, context) =>
      patchMembershipCache(qc, (rows) =>
        rows.map((r) => (r.id === context?.pendingId && created?.id ? created : r)),
      ),
    onSettled: () => markMembershipsStale(qc),
  });
};

export const useGroupMemberUpdateMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<GroupMember> & { id: number; fieldMask: string[] }) =>
      await UserClientService.UpdateGroupMember(GroupMember.create(input)),
    onMutate: async (input) => {
      let previous: GroupMember | undefined;
      patchMembershipCache(qc, (rows) =>
        rows.map((r) => {
          if (r.id !== input.id) return r;
          previous = r;
          return GroupMember.create({ ...r, ...(input as Partial<GroupMember>) });
        }),
      );
      await qc.cancelQueries({ queryKey: [queryKeys.groupMember.root] });
      return { previous };
    },
    onError: (_error, _input, context) => {
      const previous = context?.previous;
      if (!previous) return;
      patchMembershipCache(qc, (rows) => rows.map((r) => (r.id === previous.id ? previous : r)));
    },
    onSettled: () => markMembershipsStale(qc),
  });
};

export const useGroupMemberDeleteMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) =>
      await UserClientService.DeleteGroupMember(GroupMember.create({ id })),
    // Marked inactive rather than dropped. Whether the server deletes the row
    // or deactivates it decides how the person is added back — insert, or
    // revive the row the unique key would otherwise collide with — and the
    // refetch settles which. Until then, inactive is the answer that keeps the
    // revive path reachable; a caller that guesses wrong falls back.
    //
    // In onMutate, not onSuccess: patched on success the card stayed on screen
    // for the whole round trip, so the × read as having done nothing and an
    // in-flight read could put the person back.
    onMutate: async (id) => {
      let previous: GroupMember | undefined;
      patchMembershipCache(qc, (rows) =>
        rows.map((r) => {
          if (r.id !== id) return r;
          previous = r;
          return GroupMember.create({ ...r, isActive: false });
        }),
      );
      await qc.cancelQueries({ queryKey: [queryKeys.groupMember.root] });
      return { previous };
    },
    onError: (_error, _id, context) => {
      const previous = context?.previous;
      if (!previous) return;
      patchMembershipCache(qc, (rows) => rows.map((r) => (r.id === previous.id ? previous : r)));
    },
    onSettled: () => markMembershipsStale(qc),
  });
};

// ─── Combined view model for rendering the chart ─────────────────────

/**
 * Everything the chart needs to draw team pages, in lookup form.
 *
 * Deliberately one hook: the renderer needs all four entities together on every
 * frame, and fetching them separately inside the tree components would fan out
 * into a request per node.
 */
export const useOrgChartStructure = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data: memberData, isPending: membersPending } = useGroupMemberListQuery({ enabled });
  const {
    groups,
    pages,
    childrenByParent,
    isPending: groupsPending,
  } = useOrganizationGroups({ enabled });
  const { data: roleData, isPending: rolesPending } = useRoleListQuery({ enabled });
  const { categories, isPending: categoriesPending } = useRoleCategoriesByRank({ enabled });

  return useMemo(() => {
    const memberships = (memberData?.results ?? []).filter((m) => m.isActive);
    const roles = (roleData?.results ?? []).filter((r) => r.isActive);

    const groupById = new Map(groups.map((g) => [g.id, g]));
    const roleById = new Map(roles.map((r) => [r.id, r]));
    const categoryById = new Map(categories.map((c) => [c.id, c]));

    // A role's rung, which is what orders siblings on the chart.
    const levelByRoleId = new Map<number, number>();
    for (const r of roles) {
      const rung = categoryById.get(r.roleCategoryId)?.sortOrder;
      if (rung !== undefined) levelByRoleId.set(r.id, rung);
    }

    const userIdsByGroup = new Map<number, Set<number>>();
    const membershipsByUser = new Map<number, typeof memberships>();
    for (const m of memberships) {
      const set = userIdsByGroup.get(m.organizationGroupId) ?? new Set<number>();
      set.add(m.userId);
      userIdsByGroup.set(m.organizationGroupId, set);

      const list = membershipsByUser.get(m.userId) ?? [];
      list.push(m);
      membershipsByUser.set(m.userId, list);
    }

    return {
      groups,
      pages,
      childrenByParent,
      groupById,
      roleById,
      categoryById,
      levelByRoleId,
      userIdsByGroup,
      membershipsByUser,
      isPending: membersPending || groupsPending || rolesPending || categoriesPending,
    };
  }, [
    memberData,
    roleData,
    categories,
    groups,
    pages,
    childrenByParent,
    membersPending,
    groupsPending,
    rolesPending,
    categoriesPending,
  ]);
};
